/**
 * Deployment Governance
 * =====================
 * Release management, deployment, and rollback procedures.
 * 
 * Route: /wyldecrest/helpdesk/deployment-governance
 */

import React from 'react';
import GovernancePageLayout, { 
  DocumentSection, 
  SOPStep, 
  Checklist, 
  EscalationPath,
  WarningBox,
  InfoBox 
} from './GovernancePageLayout';
import { 
  Server, 
  GitBranch, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  RotateCcw
} from 'lucide-react';

const sections = [
  { id: 'release-management', title: 'Release Management' },
  { id: 'deployment-process', title: 'Deployment Process' },
  { id: 'smoke-testing', title: 'Smoke Testing' },
  { id: 'rollback-procedures', title: 'Rollback Procedures' },
  { id: 'environment-management', title: 'Environment Management' },
];

export default function DeploymentGovernancePage() {
  return (
    <GovernancePageLayout
      title="Deployment Governance"
      subtitle="Release management, deployment, and rollback procedures"
      icon={Server}
      version="2.0"
      lastReviewed="2026-05-23"
      reviewedBy="Technical Lead"
      sections={sections}
    >
      {/* Release Management */}
      <DocumentSection title="Release Management" icon={GitBranch} id="release-management">
        <p className="text-slate-300 mb-6">
          All releases follow a structured process to ensure quality and traceability.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Version Numbering</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <p className="text-slate-300 mb-3">
            Versions follow <span className="text-emerald-400 font-mono">MAJOR.MINOR.PATCH-LABEL</span> format:
          </p>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Component</th>
                <th className="pb-2">When Incremented</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-red-400">MAJOR</td>
                <td>Breaking changes, major features</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-amber-400">MINOR</td>
                <td>New features, backwards compatible</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-emerald-400">PATCH</td>
                <td>Bug fixes, minor improvements</td>
              </tr>
              <tr>
                <td className="py-2 font-mono text-blue-400">LABEL</td>
                <td>RC (Release Candidate), HOTFIX, etc.</td>
              </tr>
            </tbody>
          </table>
          <p className="text-slate-400 text-sm mt-3">
            Example: <span className="font-mono text-white">2.0.0-RC1</span> (Version 2.0.0, Release Candidate 1)
          </p>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Release Types</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
            <h4 className="text-emerald-300 font-semibold mb-2">Standard Release</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Planned features and fixes</li>
              <li>• Full testing cycle</li>
              <li>• Scheduled deployment window</li>
              <li>• 5-day lead time minimum</li>
            </ul>
          </div>
          
          <div className="bg-amber-900/20 border border-amber-700/50 rounded-lg p-4">
            <h4 className="text-amber-300 font-semibold mb-2">Hotfix Release</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Critical bug fixes only</li>
              <li>• Targeted testing</li>
              <li>• Expedited deployment</li>
              <li>• Same-day deployment possible</li>
            </ul>
          </div>
          
          <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
            <h4 className="text-blue-300 font-semibold mb-2">Emergency Release</h4>
            <ul className="text-sm text-slate-300 space-y-1">
              <li>• Security or critical issues</li>
              <li>• Minimal testing (smoke only)</li>
              <li>• Immediate deployment</li>
              <li>• Director approval required</li>
            </ul>
          </div>
        </div>
      </DocumentSection>

      {/* Deployment Process */}
      <DocumentSection title="Deployment Process" icon={Server} id="deployment-process">
        <p className="text-slate-300 mb-6">
          All deployments follow a controlled process to minimize risk.
        </p>

        <SOPStep 
          number={1}
          title="Pre-Deployment"
          description="Preparation before deployment begins."
          substeps={[
            'Verify all tests pass',
            'Complete code review',
            'Update changelog',
            'Notify stakeholders of upcoming deployment',
            'Confirm deployment window'
          ]}
        />
        
        <SOPStep 
          number={2}
          title="Database Backup"
          description="Ensure data safety before changes."
          substeps={[
            'Verify latest automated backup exists',
            'Optionally create manual snapshot',
            'Document restore point timestamp'
          ]}
        />
        
        <SOPStep 
          number={3}
          title="Deploy"
          description="Execute the deployment."
          substeps={[
            'Initiate deployment via platform',
            'Monitor deployment progress',
            'Wait for health checks to pass',
            'Verify services are running'
          ]}
        />

        <SOPStep 
          number={4}
          title="Smoke Test"
          description="Verify core functionality."
          substeps={[
            'Execute smoke test checklist',
            'Verify critical user flows',
            'Check external integrations',
            'Confirm no error spikes in logs'
          ]}
        />

        <SOPStep 
          number={5}
          title="Post-Deployment"
          description="Complete the deployment process."
          substeps={[
            'Update deployment record',
            'Notify stakeholders of completion',
            'Monitor for 30 minutes',
            'Close deployment window'
          ]}
        />

        <Checklist 
          title="Pre-Deployment Checklist"
          items={[
            'All automated tests passing',
            'Code review completed and approved',
            'Changelog updated with release notes',
            'Database migrations reviewed (if any)',
            'Backup verified within last 24 hours',
            'Deployment window communicated to team',
            'Rollback plan documented'
          ]}
        />
      </DocumentSection>

      {/* Smoke Testing */}
      <DocumentSection title="Smoke Testing" icon={CheckCircle} id="smoke-testing">
        <p className="text-slate-300 mb-6">
          Smoke tests verify core functionality after deployment.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Backend Smoke Tests</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 font-mono text-sm mb-6">
          <pre className="text-slate-300 whitespace-pre-wrap">
{`# 1. Health check
curl -s $API_URL/api/health

# 2. Authentication
curl -c cookies.txt -X POST "$API_URL/api/auth/login" \\
  -H "Content-Type: application/json" \\
  -d '{"email":"test@example.com","password":"..."}'

# 3. Authenticated endpoint
curl -b cookies.txt "$API_URL/api/auth/me"

# 4. Session verification
curl -b cookies.txt "$API_URL/api/auth/session"

# 5. Core API (jobs list)
curl -b cookies.txt "$API_URL/api/fm/jobs?limit=5"

# 6. Scheduler status
curl "$API_URL/api/scheduler/status"`}
          </pre>
        </div>

        <h3 className="text-lg font-semibold text-white mb-4">Frontend Smoke Tests</h3>
        
        <Checklist 
          title="Frontend Verification"
          items={[
            'Login page loads without errors',
            'Login with test credentials succeeds',
            'Dashboard displays data correctly',
            'Navigation to all main sections works',
            'No console errors in browser',
            'Logout clears session properly'
          ]}
        />

        <h3 className="text-lg font-semibold text-white mb-4">Integration Smoke Tests</h3>
        
        <Checklist 
          title="Integration Verification"
          items={[
            'Email notifications send correctly',
            'WebSocket connections establish',
            'File uploads succeed',
            'Database queries return data',
            'Scheduled jobs are registered'
          ]}
        />
      </DocumentSection>

      {/* Rollback Procedures */}
      <DocumentSection title="Rollback Procedures" icon={RotateCcw} id="rollback-procedures">
        <p className="text-slate-300 mb-6">
          If a deployment causes issues, rollback procedures restore the previous version.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">When to Rollback</h3>
        
        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-6">
          <ul className="text-slate-300 space-y-2">
            <li>• <strong className="text-red-300">Login failures</strong> affecting multiple users</li>
            <li>• <strong className="text-red-300">Error rate</strong> exceeding 5% for 10+ minutes</li>
            <li>• <strong className="text-red-300">Data corruption</strong> detected</li>
            <li>• <strong className="text-red-300">Critical security</strong> vulnerability discovered</li>
            <li>• <strong className="text-red-300">Core functionality</strong> broken</li>
          </ul>
        </div>

        <SOPStep 
          number={1}
          title="Decision"
          description="Confirm rollback is necessary."
          substeps={[
            'Verify issue is deployment-related',
            'Assess impact and urgency',
            'Get approval (Team Lead or higher)',
            'Document decision and reason'
          ]}
        />

        <SOPStep 
          number={2}
          title="Notify"
          description="Alert stakeholders."
          substeps={[
            'Notify operations team',
            'Post in team channel',
            'Prepare client communication if needed'
          ]}
        />

        <SOPStep 
          number={3}
          title="Execute Rollback"
          description="Use platform rollback feature."
          substeps={[
            'Navigate to deployment history',
            'Select last known good version',
            'Initiate rollback',
            'Monitor progress'
          ]}
        />

        <SOPStep 
          number={4}
          title="Verify"
          description="Confirm rollback successful."
          substeps={[
            'Run smoke tests',
            'Verify issue is resolved',
            'Monitor for 30 minutes',
            'Document outcome'
          ]}
        />

        <InfoBox title="Rollback Feature">
          Use the platform's "Rollback" feature in the chat interface. This automatically
          restores the previous checkpoint without requiring manual intervention.
        </InfoBox>

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Rollback Authority</h3>
        <EscalationPath levels={['Any Developer (initiates)', 'Team Lead (approves)', 'Director (notified)']} />
      </DocumentSection>

      {/* Environment Management */}
      <DocumentSection title="Environment Management" icon={Shield} id="environment-management">
        <p className="text-slate-300 mb-6">
          Environment configuration and management standards.
        </p>

        <h3 className="text-lg font-semibold text-white mb-4">Environment Configuration</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4 mb-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Variable</th>
                <th className="pb-2">Purpose</th>
                <th className="pb-2">Required</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-emerald-400">MONGO_URL</td>
                <td>Database connection</td>
                <td>Yes</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-emerald-400">JWT_SECRET</td>
                <td>Token signing (32+ chars in prod)</td>
                <td>Yes</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-blue-400">STRIPE_API_KEY</td>
                <td>Payment processing</td>
                <td>Yes</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2 font-mono text-blue-400">RESEND_API_KEY</td>
                <td>Email sending</td>
                <td>Production</td>
              </tr>
              <tr>
                <td className="py-2 font-mono text-amber-400">APP_ENVIRONMENT</td>
                <td>Environment flag</td>
                <td>Recommended</td>
              </tr>
            </tbody>
          </table>
        </div>

        <WarningBox title="Protected Variables">
          MONGO_URL, JWT_SECRET, and REACT_APP_BACKEND_URL are pre-configured and should
          not be modified without explicit approval and testing.
        </WarningBox>

        <h3 className="text-lg font-semibold text-white mb-4 mt-6">Security Settings</h3>
        
        <div className="bg-slate-900/50 rounded-lg p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-700">
                <th className="pb-2">Setting</th>
                <th className="pb-2">Development</th>
                <th className="pb-2">Production</th>
              </tr>
            </thead>
            <tbody className="text-slate-300">
              <tr className="border-b border-slate-800">
                <td className="py-2">Cookie Secure Flag</td>
                <td>false</td>
                <td className="text-emerald-400">true</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Cookie SameSite</td>
                <td>lax</td>
                <td>lax</td>
              </tr>
              <tr className="border-b border-slate-800">
                <td className="py-2">Rate Limiting</td>
                <td>Disabled</td>
                <td className="text-emerald-400">Enabled</td>
              </tr>
              <tr>
                <td className="py-2">CORS Origins</td>
                <td>Wildcard</td>
                <td className="text-emerald-400">Restricted</td>
              </tr>
            </tbody>
          </table>
        </div>
      </DocumentSection>
    </GovernancePageLayout>
  );
}
